import type { Action, DevToolsEnhancerOptions } from "@reduxjs/toolkit"

// DevTools is an external debugging view, not the authorized application's store.
// Deny by default: request arguments, response/error bodies, draft values, conflict
// snapshots and even cache keys can contain PII. A field-name blacklist cannot
// cover them. Only code-owned action types, enum states and counts leave the store.
type RecordValue = Record<string, unknown>
function record(value: unknown): RecordValue {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as RecordValue : {}
}

const REQUEST_STATES = new Set(["pending", "fulfilled", "rejected"])
const EDITOR_PHASES = new Set([
  "opening", "editing", "validating", "saving", "reconciling", "conflict", "outcomeUnknown",
])

export function sanitizeDevToolsAction(action: Action) {
  const status = record(record(action).meta).requestStatus
  return {
    type: action.type,
    redacted: true,
    ...(typeof status === "string" && REQUEST_STATES.has(status) ? { requestStatus: status } : {}),
  }
}

function cacheSummary(value: unknown) {
  const cache = record(value)
  return {
    queryCount: Object.keys(record(cache.queries)).length,
    mutationCount: Object.keys(record(cache.mutations)).length,
  }
}

/** Never copies arbitrary keys or values, including unknown/new feature slices. */
export function sanitizeDevToolsState(state: unknown) {
  const root = record(state)
  const phase = record(record(root.benefitEditors).active).phase
  return {
    redacted: true,
    staffCache: cacheSummary(root.api),
    publicCache: cacheSummary(root.publicApi),
    editorPhase: typeof phase === "string" && EDITOR_PHASES.has(phase) ? phase : null,
  }
}

/** Production builds are not connected to DevTools at all. No persistence is enabled. */
export function devToolsOptions(nodeEnv: string | undefined): boolean | DevToolsEnhancerOptions {
  if (nodeEnv === "production") return false
  return {
    // DevTools types require shape-preserving functions even though its display
    // supports metadata projections. The application never consumes these views.
    actionSanitizer: sanitizeDevToolsAction as DevToolsEnhancerOptions["actionSanitizer"],
    stateSanitizer: sanitizeDevToolsState as DevToolsEnhancerOptions["stateSanitizer"],
  }
}
